import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  fetchTenantMe,
  pollLoginCode,
  requestLoginCode,
  requestTenantOtp,
  tenantLogout,
  verifyTenantOtp,
} from './api.js'

export const tenantMeQueryKey = ['tenant-me'] as const

/**
 * `REQ-design-the-customer-console-ux-audit` finding S1. This gate gets no
 * ceiling from the global `QueryClient` — the default retry ladder (3
 * attempts, exponential backoff) plus `networkMode: 'online'`'s own pause
 * behaviour left a customer on "Checking your session…" for 20–30 seconds
 * against an unreachable `customer-app`, the live condition for every
 * visitor until `LGH-047` (2026-09-22). One retry, one short delay: enough
 * to survive a single dropped request, not enough to read as hung.
 */
export function useTenantMe() {
  return useQuery({
    queryKey: tenantMeQueryKey,
    queryFn: fetchTenantMe,
    staleTime: 60_000,
    retry: 1,
    retryDelay: 1_000,
  })
}

export function useRequestTenantOtp() {
  return useMutation({ mutationFn: requestTenantOtp })
}

/**
 * `TST-001` finding 6. `verify-otp`'s own response carries only `{id, role}`
 * — `1C`'s own design, offered to grow later — so caching it directly under
 * `setQueryData` left the shell showing the "Your account"/no-name fallback
 * until an unrelated refetch happened to occur. `invalidateQueries` instead
 * forces the next mount (the very next screen, right after this succeeds) to
 * read the real thing from `/me`, which does carry name/email.
 */
export function useVerifyTenantOtp() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ phone, code }: { phone: string; code: string }) => verifyTenantOtp(phone, code),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tenantMeQueryKey })
    },
  })
}

export function useTenantLogout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: tenantLogout,
    onSuccess: () => {
      queryClient.setQueryData(tenantMeQueryKey, null)
    },
  })
}

export function useRequestLoginCode() {
  return useMutation({ mutationFn: requestLoginCode })
}

/**
 * `LGH-050`, `PLT-106`. Plain polling — `1C`'s own instruction, matching
 * `LGH-049`'s "real time doesn't require push" reasoning. `refetchInterval`
 * stops itself once the code is settled (`confirmed`/`expired`): there is
 * nothing left to learn by continuing to ask, and a `confirmed` response has
 * already claimed the one session it grants — polling past it would only
 * ever hit the `404`-mapped-to-`expired` case `pollLoginCode` already
 * handles, never a second real answer.
 *
 * **3s interval** — `1C`'s own REQ asked what cadence to size the rate limit
 * against; this is well inside the 5/min IP-keyed budget `request-login-
 * code` itself uses (this endpoint isn't limited by that one at all, since
 * it's a different route, but 3s keeps the request volume modest regardless)
 * and short enough that a 10-minute code's own TTL is the real time budget,
 * not the poll cadence.
 */
export function useLoginCodeStatus(code: string | null) {
  return useQuery({
    queryKey: ['login-code-status', code],
    queryFn: () => pollLoginCode(code ?? ''),
    enabled: code !== null,
    refetchInterval: (query) => {
      const status = query.state.data?.status
      if (status === 'confirmed' || status === 'expired') return false
      return 3_000
    },
  })
}
